import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button, AutoComplete, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { setFilters } from "../expenseBalanceSlice";
import { fetchExpenseBalance, searchAccounts } from "../expenseBalanceThunk";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import { useLocation, useSearchParams } from "react-router-dom";
import { currentDateEndTime, currentDateStartTime } from "../../../../lib/functions";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const ExpenseBalanceFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.expenseBalance);
  const { searchResults, loadingSearch } = useSelector(
    (state) => state.expenseBalance
  );

  const [types] = useState([
    { type: "All Expense Account" },
    { type: "Expense Account Wise" },
    { type: "Date Wise" },
  ]);

  const [accountName, setAccountName] = useState("");

  useDefaultDateRange(setFilters, filters, "today");

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const dateFrom = searchParams.get("dateFrom");
    const dateTo = searchParams.get("dateTo");

    if (dateFrom && dateTo) {
      const selectedType = { type: "Date Wise" };
      dispatch(setFilters({
        selectedType,
        selectedAccount: null,
        fromDate: dateFrom,
        toDate: dateTo,
      }));
      dispatch(fetchExpenseBalance({
        selectedType,
        selectedAccount: null,
        fromDate: dateFrom,
        toDate: dateTo,
      }));
      return;
    }

    const path = pathname.split("/")[3];
    if (path) {
      handleFilterChange("filterType", "Date Wise");
      handleFilterChange("toDate", path);
      handleReport();
    }
  }, []);

  useEffect(() => {
  if (typeof accountName === "string" && accountName.trim() !== "") {
    dispatch(searchAccounts(accountName));
  }
}, [accountName, dispatch]);


  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.selectedType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    if (
      filters.selectedType.type === "Expense Account Wise" &&
      !filters.selectedAccount
    ) {
      messageApi.warning("Select Account");
      return;
    }

    if (
      filters.selectedType.type === "Date Wise" &&
      (!filters.fromDate || !filters.toDate)
    ) {
      messageApi.warning("Select Date Range");
      return;
    }

    const payload = {
      selectedType: filters.selectedType,
      selectedAccount: filters.selectedAccount,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    };

    dispatch(fetchExpenseBalance(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch report ❌");
      }
    });
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={16} style={{ alignItems: "center" }}>
        <Col span={4}>
          <label>Filter Type</label>
          <AutoComplete
            style={{ width: "100%" }}
            options={types.map((item) => ({
              label: item.type,
              value: item.type,
              obj: item,
            }))}
            value={filters.selectedType?.type}
            onSelect={(value, option) => {
              handleFilterChange("selectedType", option.obj);
            }}
          >
            <Input placeholder="Filter Type" />
          </AutoComplete>
        </Col>

        {(filters.selectedType?.type === "Expense Account Wise" ||
          filters.selectedType?.type === "Date Wise") && (
          <Col span={4}>
            <label>Search Account</label>
            <SearchListAutoComplete
              entityType="expense"
              searchThunk={(text) => setAccountName(text)}
              resultSelector={(state) => state.expenseBalance.searchResults}
              loading={loadingSearch}
              onSelect={(obj) => {
                handleFilterChange("selectedAccount", obj);
              }}
              placeholder="Search Account"
              showAddNew={false}
            />
          </Col>
        )}

        {filters.selectedType?.type === "Date Wise" && (
          <CommonDateRangeFilter
            filters={filters}
            onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
            }}
          />
        )}

        <Col
          span={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            block
            style={{height: 35.19}}
            data-testid="report-btn"
          >
            Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default ExpenseBalanceFilters;
